/**
 * Resolves a persisted `ChartConfig` column reference against the CURRENT
 * result set's columns before the config is used to read rows.
 *
 * Row-key rekeying (col_<i> for a duplicate/blank name, see
 * `electron/query-engine-adapters/duplicateColumnDedup.ts`) means a chart
 * config saved with an older `.ipynb`/dashboard tile — or built before the
 * rekeying landed — can hold either a row KEY or a plain column NAME
 * (including a whitespace-only name, e.g. `' '` — a genuinely BLANK name
 * (`''`) can't come back as a ref here: `analyzeColumns` labels it
 * `Column N`, and the builder can never persist an empty string, so `''`
 * matches neither a key nor a name and is simply left unresolved like any
 * other unrecognized reference). Resolve in this order:
 *   1. exact key match wins (the current, correct shape)
 *   2. else a NAME that matches exactly one column resolves to its key
 *   3. else the reference is left unresolved (as-is) — validation reports it
 *
 * This is the single source of truth for that rule: the chart data-shaping
 * seam (`useChartData`) and the MCP `notebook_set_chart_config` resolver
 * both call it instead of keeping their own copies.
 */
import type { ChartConfig } from '../types/chartTypes';

/** A result column as available for resolving a chart config's column references. */
export type ChartRefColumn = { key: string; name: string };

/**
 * Resolves one column reference to its row key. Returns `ref` unchanged when
 * it is already a valid key, or when it cannot be resolved (not found, or a
 * name shared by more than one column).
 */
export function resolveChartColumnRef(ref: string, columns: readonly ChartRefColumn[]): string {
  if (columns.some((c) => c.key === ref)) return ref;

  const nameMatches = columns.filter((c) => c.name === ref);
  if (nameMatches.length === 1) return nameMatches[0].key;

  return ref;
}

/**
 * Resolves every column reference in a `ChartConfig` (`xAxis.column`, each
 * `yAxis[].column`, `series.column`) to row keys. Returns the same object
 * reference when nothing needed resolving, so memoized consumers do not
 * re-render.
 */
export function resolveChartConfigColumnRefs(
  config: ChartConfig,
  columns: readonly ChartRefColumn[]
): ChartConfig {
  if (columns.length === 0) return config;

  const resolvedXColumn = config.xAxis?.column
    ? resolveChartColumnRef(config.xAxis.column, columns)
    : config.xAxis?.column;
  const xAxis =
    config.xAxis && resolvedXColumn !== config.xAxis.column
      ? { ...config.xAxis, column: resolvedXColumn }
      : config.xAxis;

  let yAxisChanged = false;
  const resolvedYAxis = config.yAxis.map((y) => {
    if (!y.column) return y;
    const resolved = resolveChartColumnRef(y.column, columns);
    if (resolved === y.column) return y;
    yAxisChanged = true;
    return { ...y, column: resolved };
  });
  const yAxis = yAxisChanged ? resolvedYAxis : config.yAxis;

  let series = config.series;
  if (config.series?.column) {
    const resolved = resolveChartColumnRef(config.series.column, columns);
    if (resolved !== config.series.column) {
      series = { ...config.series, column: resolved };
    }
  }

  if (xAxis === config.xAxis && !yAxisChanged && series === config.series) {
    return config;
  }

  return { ...config, xAxis, yAxis, series };
}
